interface SearchResult {
  id: number;
  title: string;
  excerpt: string;
  url: string;
  date: string;
  category: string;
}

class SearchModal {
  private modal: HTMLDivElement | null = null;
  private searchInput: HTMLInputElement | null = null;
  private resultsContainer: HTMLDivElement | null = null;
  private isOpen = false;

  constructor() {
    this.init();
  }

  private init(): void {
    this.createModal();
    this.bindEvents();
  }

  private createModal(): void {
    const modalHTML = `
      <div id="search-modal" class="fixed inset-0 z-[200] hidden" role="dialog" aria-modal="true" aria-label="Buscar">
        <div id="search-backdrop" class="absolute inset-0 bg-black/75" style="backdrop-filter:blur(4px)"></div>

        <div class="relative z-10 w-full max-w-[660px] mx-auto px-4" style="margin-top:10vh">

          <div class="bg-white">

            <!-- Campo de busca -->
            <div class="flex items-center gap-3 px-5 py-4" style="border-bottom:1px solid #f0f0f0">
              <svg class="w-5 h-5 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="color:#155E31" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <input
                type="text"
                id="search-input"
                placeholder="Buscar notícias..."
                autocomplete="off"
                class="flex-1 text-[1.05rem] text-gray-900 placeholder-gray-300 bg-transparent outline-none"
              >
              <div id="search-spinner" class="hidden shrink-0">
                <svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24" style="color:#155E31">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <button id="close-search" aria-label="Fechar busca" class="shrink-0 p-1 transition-opacity hover:opacity-50" style="color:#ccc">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- Resultados -->
            <div id="search-results" style="max-height:62vh;overflow-y:auto">
              <p class="px-5 py-8 text-center text-sm" style="color:#bbb">
                Digite ao menos 2 caracteres para buscar…
              </p>
            </div>

          </div>

          <p class="text-center text-xs mt-3" style="color:rgba(255,255,255,.35)">
            ESC para fechar
          </p>

        </div>
      </div>
    `;

    // Insert modal into DOM
    document.body.insertAdjacentHTML('beforeend', modalHTML);

    // Get references
    this.modal = document.getElementById('search-modal') as HTMLDivElement;
    this.searchInput = document.getElementById('search-input') as HTMLInputElement;
    this.resultsContainer = document.getElementById('search-results') as HTMLDivElement;
  }

  private bindEvents(): void {
    // Open modal
    const searchToggle = document.getElementById('search-toggle');
    if (searchToggle) {
      searchToggle.addEventListener('click', () => this.openModal());
    }

    // Close modal
    const closeButton = document.getElementById('close-search');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.closeModal());
    }

    // Close on backdrop click
    const backdrop = document.getElementById('search-backdrop');
    if (backdrop) {
      backdrop.addEventListener('click', () => this.closeModal());
    }

    // Search input events
    if (this.searchInput) {
      let searchTimeout: NodeJS.Timeout;

      this.searchInput.addEventListener('input', (e) => {
        const query = (e.target as HTMLInputElement).value.trim();

        // Clear previous timeout
        clearTimeout(searchTimeout);

        if (query.length < 2) {
          this.showPlaceholder();
          return;
        }

        // Show spinner
        this.showSpinner();

        // Debounce search
        searchTimeout = setTimeout(() => {
          this.performSearch(query);
        }, 300);
      });

      // Handle Enter key
      this.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const query = this.searchInput?.value.trim();
          if (query && query.length >= 2) {
            this.performSearch(query);
          }
        }
      });
    }

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeModal();
      }
    });
  }

  private openModal(): void {
    if (this.modal) {
      this.modal.classList.remove('hidden');
      this.isOpen = true;

      // Focus on input
      setTimeout(() => {
        this.searchInput?.focus();
      }, 100);
    }
  }

  private closeModal(): void {
    if (this.modal) {
      this.modal.classList.add('hidden');
      this.isOpen = false;

      // Clear search
      if (this.searchInput) {
        this.searchInput.value = '';
      }
      this.showPlaceholder();
    }
  }

  private showSpinner(): void {
    const spinner = document.getElementById('search-spinner');
    if (spinner) {
      spinner.classList.remove('hidden');
    }
  }

  private hideSpinner(): void {
    const spinner = document.getElementById('search-spinner');
    if (spinner) {
      spinner.classList.add('hidden');
    }
  }

  private showPlaceholder(): void {
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = `
        <p class="px-5 py-8 text-center text-sm" style="color:#bbb">
          Digite ao menos 2 caracteres para buscar…
        </p>
      `;
    }
  }

  private async performSearch(query: string): Promise<void> {
    try {
      const response = await fetch(`/wp-json/wp/v2/posts?search=${encodeURIComponent(query)}&per_page=10&_embed`);

      if (!response.ok) {
        throw new Error('Erro na busca');
      }

      const posts = await response.json();
      this.displayResults(posts, query);

    } catch (error) {
      console.error('Erro na busca:', error);
      this.showError();
    } finally {
      this.hideSpinner();
    }
  }

  private displayResults(posts: any[], query: string): void {
    if (!this.resultsContainer) return;

    if (posts.length === 0) {
      this.resultsContainer.innerHTML = `
        <p class="px-5 py-8 text-center text-sm" style="color:#bbb">
          Nenhum resultado para "<strong style="color:#555">${query}</strong>"
        </p>
      `;
      return;
    }

    const resultsHTML = posts.map((post, i) => {
      const title    = post.title?.rendered ?? '';
      const date     = new Date(post.date).toLocaleDateString('pt-BR', { day:'2-digit', month:'short', year:'numeric' });
      const category = post._embedded?.['wp:term']?.[0]?.[0]?.name ?? '';
      const thumb    = post._embedded?.['wp:featuredmedia']?.[0]?.media_details?.sizes?.thumbnail?.source_url
                    ?? post._embedded?.['wp:featuredmedia']?.[0]?.source_url ?? '';
      const border   = i < posts.length - 1 ? 'border-bottom:1px solid #f4f4f4' : '';

      return `
        <a href="${post.link}" style="${border}" class="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors block group">
          ${thumb ? `<img src="${thumb}" alt="" class="w-[70px] h-[52px] object-cover shrink-0" loading="lazy">` : ''}
          <div class="flex-1 min-w-0">
            ${category ? `<span class="block text-[10px] font-black uppercase tracking-[0.15em] mb-1" style="color:#155E31">${category}</span>` : ''}
            <h4 class="text-[0.85rem] font-bold leading-snug line-clamp-2 group-hover:text-primary transition-colors" style="color:#111">${title}</h4>
            <span class="text-[11px] mt-1 block" style="color:#bbb">${date}</span>
          </div>
          <svg class="w-4 h-4 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="color:#155E31" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
          </svg>
        </a>
      `;
    }).join('');

    this.resultsContainer.innerHTML = resultsHTML;
  }

  private showError(): void {
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = `
        <p class="px-5 py-8 text-center text-sm" style="color:#e10719">
          Erro ao realizar a busca. Tente novamente.
        </p>
      `;
    }
  }
}

// Initialize search modal when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new SearchModal();
}); 